/**
 * Theme context — manages dark/light/auto theme with localStorage persistence.
 *
 * - "auto"  → follows the OS `prefers-color-scheme` media query
 * - "dark"  → forces dark theme
 * - "light" → forces light theme
 *
 * The active theme is applied by toggling the `dark` / `light` class on
 * `<html>`.  An inline script in `index.html` sets the initial class BEFORE
 * React mounts to prevent a flash of the wrong theme (FOUC).
 *
 * @module apps/web/contexts/theme
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

export type ThemePreference = 'auto' | 'dark' | 'light';
export type ResolvedTheme = 'dark' | 'light';

interface ThemeContextValue {
  /** What the user explicitly chose. */
  preference: ThemePreference;
  /** The actual theme currently applied (resolved from preference + OS). */
  resolved: ResolvedTheme;
  /** Change the preference. Persisted to localStorage. */
  setPreference: (pref: ThemePreference) => void;
  /** Convenience toggle — cycles auto → dark → light → auto. */
  toggle: () => void;
}

const STORAGE_KEY = 'r42-theme';
const DEFAULT_PREFERENCE: ThemePreference = 'auto';

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** Read the OS color scheme preference. */
function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Read the stored preference from localStorage (safe for SSR / private mode). */
function readStoredPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light' || stored === 'auto') return stored;
  } catch { /* noop */ }
  return DEFAULT_PREFERENCE;
}

/** Apply the resolved theme to the <html> element. */
function applyThemeClass(resolved: ResolvedTheme): void {
  const html = document.documentElement;
  html.classList.remove('dark', 'light');
  html.classList.add(resolved);
  // Also set a data attribute for non-Tailwind CSS that can't use class variants
  html.setAttribute('data-theme', resolved);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => readStoredPreference());
  const [resolved, setResolved] = useState<ResolvedTheme>(() => {
    const pref = readStoredPreference();
    return pref === 'auto' ? getSystemTheme() : pref;
  });

  // Apply theme class whenever the resolved theme changes
  useEffect(() => {
    applyThemeClass(resolved);
  }, [resolved]);

  // Listen for OS theme changes when preference is "auto"
  useEffect(() => {
    if (preference !== 'auto') return;

    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      setResolved(e.matches ? 'dark' : 'light');
    };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [preference]);

  // Resolve theme whenever preference changes
  useEffect(() => {
    setResolved(preference === 'auto' ? getSystemTheme() : preference);
  }, [preference]);

  const setPreference = useCallback((pref: ThemePreference) => {
    setPreferenceState(pref);
    try { localStorage.setItem(STORAGE_KEY, pref); } catch { /* noop */ }
  }, []);

  const toggle = useCallback(() => {
    setPreferenceState(prev => {
      const next: ThemePreference =
        prev === 'auto' ? 'dark' :
        prev === 'dark' ? 'light' : 'auto';
      try { localStorage.setItem(STORAGE_KEY, next); } catch { /* noop */ }
      return next;
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ preference, resolved, setPreference, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
